<template>
  <section class="custom-repair-wrapper">
    <!-- the listing gives way to the detail view, the way every other detail in this app works -->
    <template v-if="!detailRow">
    <div class="row">
      <div class="col">
        <p>
          This facility lists the Repair (broken) Links reported by every ADAS Map application. Narrow the list by Application, Year, Make, and/or
          Model.
        </p>
      </div>
      <div class="col">
        <z-message v-model="message" :timeout="7000" class="alert alert-warning" />
      </div>
    </div>
    <!-- Filters -->
    <div v-if="isFetched" class="row mt-3">
      <div class="col-6">
        <div class="d-flex gap-3">
          <div class="input-group custom-filter">
            <span class="input-group-text">Search</span>
            <input v-model="searchTerm" type="search" class="form-control" />
          </div>
          <div class="input-group custom-filter">
            <span class="input-group-text">Application</span>
            <Multiselect
              ref="sources"
              v-model="filterSources"
              class="form-control custom-multiselect"
              placeholder="all"
              :open-direction="'bottom'"
              :close-on-select="false"
              :options="sources"
              mode="tags"
              label="name"
              value-prop="value"
            />
          </div>
          <div class="input-group custom-filter">
            <span class="input-group-text">Status</span>
            <select v-model="filterStatus" class="form-control">
              <option v-for="item of statuses" :key="item.id" :value="item.value">{{ item.name }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="col-6">
        <div class="d-flex gap-3">
          <div class="input-group custom-filter">
            <span class="input-group-text">Year</span>
            <select v-model="filterYear" class="form-control">
              <option v-for="item of viewYears" :key="item.id" :value="item.value">{{ item.name }}</option>
            </select>
          </div>
          <div class="input-group custom-filter">
            <span class="input-group-text">Make</span>
            <select v-model="filterMake" class="form-control">
              <option v-for="item of makes" :key="item.id" :value="item.value">{{ item.name }}</option>
            </select>
          </div>
          <div class="input-group custom-filter">
            <span class="input-group-text">Model</span>
            <select v-model="filterModel" class="form-control">
              <option v-for="item of models" :key="item.id" :value="item.value">{{ item.name }}</option>
            </select>

          </div>
          <button class="btn btn-outline-secondary custom-filter-clear" @click="clearFilters">Clear</button>
        </div>
      </div>
    </div>
    <!-- Actions -->
    <div v-if="isFetched" class="row mt-3 align-items-center">
      <div class="col-auto">
        <div class="btn-toolbar">
          <div class="btn-group">
            <button class="btn btn-primary btn-sm" :disabled="filteredLinks.length === 0" @click="refresh">Refresh</button>
          </div>
          <div class="btn-group ms-2">
            <button class="btn btn-primary btn-sm" :disabled="isValidating || filteredLinks.length === 0" @click="validate">
              <FontAwesomeIcon v-if="isValidating" icon="fa-solid fa-spinner" spin class="me-2" />
              Validate
            </button>
          </div>
          <div class="btn-group ms-2">
            <button class="btn btn-primary btn-sm" :disabled="isDownloading || filteredLinks.length === 0" @click="saveCsv">
              <FontAwesomeIcon v-if="isDownloading" icon="fa-solid fa-spinner" spin class="me-2" />
              Export as CSV
            </button>
          </div>
        </div>
      </div>
      <!-- Paging -->
      <template v-if="filteredLinks.length > 0">
        <div class="col"></div>
        <div class="col-auto">
          <div class="input-group input-group-sm custom-page-size">
            <select v-model="pageSize" class="form-control">
              <option v-for="item of pageSizes" :key="item.value" :value="item.value">{{ item.name }}</option>
            </select>
          </div>
        </div>
        <div class="col-auto">
          <nav class="custom-pagination">
            <z-paginate v-model="page" :page-count="pages" />
          </nav>
        </div>
        <div class="col-auto">{{ filteredLinks.length }} items</div>
      </template>
    </div>
    <!-- Listing -->
    <div v-if="isFetched || loading.brokenLinks" class="row mt-2">
      <div class="col">
        <div v-show="loading.brokenLinks" class="custom-spinner">
          <FontAwesomeIcon icon="fa-solid fa-spinner me-2" spin />
        </div>
        <div v-show="!loading.brokenLinks" class="listingWrapper">
          <z-table
            :model-value="pagedLinks"
            class="table table-bordered table-striped"
            :columns="tableColumns"
            :sort-by="orderBy"
            :order-by="sortBy"
            :is-header-fixed="true"
            @sort="setOrder"
          >
            <template #tbody="props">
              <td>{{ appName(props.row.source) }}</td>
              <td>{{ props.row.adasInspection }}</td>
              <td>{{ props.row.vehicleMakeName }}</td>
              <td>{{ props.row.vehicleModel }}</td>
              <td>{{ props.row.vehicleYear }}</td>
              <td>{{ props.row.timesReported }}</td>
              <td>{{ formatDate(props.row.createdOn) }}</td>
              <td>{{ formatDate(props.row.lastReportedOn) }}</td>
            </template>
            <template #detail="props">
              <repair-link-groups
                :groups="props.row.groups"
                :source="props.row.source"
                :validations="validations"
                :is-validated="isValidated"
                @edit="(group, link) => openDetail(props.row, group, link)"
              />
            </template>
            <template #empty> Nothing found! </template>
          </z-table>
        </div>
      </div>
    </div>
    </template>
    <!-- Detail -->
    <repair-link-detail
      v-if="detailRow"
      :model-value="detailRow"
      @closing="closeDetail"
      @saved="saveDetail"
      @update:model-value="updateDetail"
    />
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { adasmatrixStore } from '@/stores/adasmatrix'
import { appsStore } from '@/stores/apps'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import moment from 'moment'
import _ from 'lodash'
import Multiselect from '@vueform/multiselect'
import globals from '@/plugins/globals'
import { buildLinkGroups, getBrokenLinkStatus, brokenLinkStatuses, BrokenLinkStatus, clicksFor, isNoLinkBuiltOrFound } from '@/plugins/broken-link-helper'
import ZTable from '@/components/utilities/elements/Table.vue'
import ZMessage from '@/components/utilities/elements/Message.vue'
import ZPaginate from '@/components/utilities/elements/Paginate.vue'
import RepairLinkDetail from '@/components/adasmatrix/repair-link-detail.vue'
import RepairLinkGroups from '@/components/adasmatrix/repair-link-groups.vue'

const STATUS_UNRESOLVED = 'unresolved'

const LOOKUP_TYPES = [
  { value: 1, name: 'System' },
  { value: 2, name: 'Location' },
  { value: 3, name: 'Static' },
  { value: 4, name: 'Dynamic' }
]

const VALIDATION_FIXED = { name: 'Fixed', class: 'text-success font-weight-bold' }
const VALIDATION_UNCHANGED = { name: 'Not Fixed', class: 'text-danger font-weight-bold' }
const VALIDATION_NO_LINKS = { name: 'No Links', class: 'text-warning font-weight-bold' }

export default {
  name: 'RepairLinks',
  components: {
    FontAwesomeIcon,
    Multiselect,
    ZTable,
    ZMessage,
    ZPaginate,
    RepairLinkDetail,
    RepairLinkGroups
  },
  data() {
    return {
      selectedApps: [],
      filterSources: [],
      filterStatus: STATUS_UNRESOLVED,
      filterYear: null,
      filterMake: null,
      filterModel: null,
      searchTerm: '',
      orderBy: 'createdOn',
      sortBy: 'desc',
      page: 1,
      pageSize: 10,
      pageSizes: [
        { name: '10', value: 10 },
        { name: '25', value: 25 },
        { name: '50', value: 50 },
        { name: '100', value: 100 }
      ],
      message: null,
      isFetched: false,
      isDownloading: false,
      isValidating: false,
      // the Validated column only earns its place once a run has filled it
      isValidated: false,
      // "<source>-<adasBrokenLink>" -> outcome, filled by validate()
      validations: {},
      // the row shown in the full-page detail view, which replaces this listing
      detailRow: null,
      isListingStale: false
    }
  },
  async mounted() {
    this.resetBrokenLinks()
    await this.getApps('ADAS')
    // default to every ADAS application and load their links straight away
    this.selectedApps = this.apps.map((x) => x.appNumber).filter((x) => x)
    await this.fetchLinks()
  },
  computed: {
    ...mapState(appsStore, ['apps']),
    ...mapState(adasmatrixStore, ['loading', 'brokenLinks']),
    // appNumber -> application, so rows can show the friendly name
    appNames() {
      return _.keyBy(this.apps, 'appNumber')
    },
    tableColumns() {
      return [
        { field: 'source', label: 'Source', sortable: true },
        { field: 'adasInspection', label: 'Inspection', sortable: true },
        { field: 'vehicleMakeName', label: 'Make', sortable: true },
        { field: 'vehicleModel', label: 'Model', sortable: true },
        { field: 'vehicleYear', label: 'Year', sortable: true },
        { field: 'timesReported', label: 'Clicks', sortable: true },
        { field: 'createdOn', label: 'Reported Date', sortable: true },
        { field: 'lastReportedOn', label: 'Last Reported Date', sortable: true }
      ]
    },
    viewYears() {
      let _data = [
        {
          id: -1,
          name: 'all',
          value: null
        }
      ]

      let now = new Date()
      let nowYear = now.getFullYear()
      let numValues = 6
      let cnt = 0
      for (let idx = numValues; idx > 0; idx--) {
        _data.push({
          id: cnt,
          name: nowYear + idx,
          value: nowYear + idx
        })
        cnt++
      }
      numValues = nowYear - 1980
      for (let idx = numValues; idx > 0; idx--) {
        _data.push({
          id: cnt,
          name: 1980 + idx,
          value: 1980 + idx
        })
        cnt++
      }

      return _data
    },
    // every ADAS application the listing fetches from - no 'all' entry, an empty selection means all
    sources() {
      let _data = []
      let names = _.uniq(this.apps.map((x) => x.appNumber).filter((x) => x !== null && x !== undefined && x !== ''))
      _.sortBy(names, (source) => this.appName(source)).forEach((source, idx) => {
        _data.push({
          id: idx,
          name: this.appName(source),
          value: source
        })
      })
      return _data
    },
    // every known status, plus the 'unresolved' shorthand the listing opens on
    statuses() {
      let _data = [
        { id: -2, name: 'unresolved', value: STATUS_UNRESOLVED },
        { id: -1, name: 'all', value: null }
      ]
      brokenLinkStatuses.forEach((status, idx) => {
        _data.push({
          id: idx,
          name: status.name,
          value: status.value
        })
      })
      return _data
    },
    // makes actually present in the fetched links
    makes() {
      let _data = [
        {
          id: 0,
          name: 'all',
          value: null
        }
      ]
      let names = _.uniq(this.brokenLinks.map((x) => x.vehicleMakeName).filter((x) => x !== null && x !== undefined && x !== '')).sort()
      names.forEach((name, idx) => {
        _data.push({
          id: idx + 1,
          name: name,
          value: name
        })
      })
      return _data
    },
    models() {
      let _data = [{
        id: -1,
        name: 'all',
        value: null
      }]
      const rows = this.filterMake ? this.brokenLinks.filter(r => r.vehicleMakeName === this.filterMake) : this.brokenLinks
      let modelNames = _.uniq(rows.map(x => x.vehicleModel)).filter(x => x !== null && x !== undefined && x !== '').sort()
      modelNames.forEach((name, idx) => {
        _data.push({
          id: idx + 1,
          name: name,
          value: name
        })
      })
      return _data
    },
    // the api returns one row per inspection, with [groups] and their [links] as raw json strings
    inspectionRows() {
      let rows = this.brokenLinks.map((row) => {
        let groups = this.parseJsonArray(row.groups).map((group) => ({
          ...group,
          links: this.parseJsonArray(group.links),
          isDetailVisible: false
        }))
        return {
          ...row,
          groups: groups,
          totalClicks: groups.reduce((total, group) => total + group.links.reduce((sum, link) => sum + clicksFor(link), 0), 0),
          searchText: this.buildSearchText(row, groups),
          isDetailVisible: false
        }
      })
      let counts = this.buildReportCounts(rows)
      rows.forEach((inspection) => {
        inspection.groups.forEach((group) => {
          group.links = group.links
            .map((link) => ({ ...link, timesReported: counts[this.reportKey(group, link)] || 1 }))
            .sort((a, b) => b.timesReported - a.timesReported)
          group.timesReported = group.links.reduce((total, link) => total + link.timesReported, 0)
        })
        inspection.groups.sort((a, b) => b.timesReported - a.timesReported)
        inspection.timesReported = inspection.groups.reduce((total, group) => total + group.timesReported, 0)
      })
      return rows
    },
    filteredLinks() {
      let _data = this.inspectionRows.filter((link) => {
        if (this.filterStatus === STATUS_UNRESOLVED) {
          if (link.status === BrokenLinkStatus.Resolved) {
            return false
          }
        } else if (this.filterStatus) {
          if (link.status !== this.filterStatus) {
            return false
          }
        }
        if (this.filterSources.length > 0) {
          if (!this.filterSources.includes(link.source)) {
            return false
          }
        }
        if (this.filterYear) {
          let year = parseInt(this.filterYear)
          let vehicleYear = parseInt(link.vehicleYear)
          if (vehicleYear !== year) {
            return false
          }
        }
        if (this.filterMake) {
          if (link.vehicleMakeName !== this.filterMake) {
            return false
          }
        }
        if (this.filterModel) {
          let model = link.vehicleModel ? link.vehicleModel.toUpperCase() : ''
          if (!model.includes(this.filterModel.trim().toUpperCase())) {
            return false
          }
        }
        if (!this.matchesSearch(link)) {
          return false
        }
        return true
      })
      return _.orderBy(_data, [this.orderBy], [this.sortBy])
    },
    pages() {
      return Math.ceil(this.filteredLinks.length / this.pageSize)
    },
    // the list can shrink under the selected page - filtering, a refresh - so never read past the end of it
    currentPage() {
      return Math.min(this.page, Math.max(this.pages, 1))
    },
    pagedLinks() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredLinks.slice(start, start + this.pageSize)
    }
  },
  watch: {
    filterSources() {
      this.page = 1
    },
    filterStatus() {
      this.page = 1
    },
    filterYear() {
      this.page = 1
    },
    filterMake() {
      this.page = 1
    },
    filterModel() {
      this.page = 1
    },
    searchTerm() {
      this.page = 1
    },
    pageSize() {
      this.page = 1
    },
    // keep the paginator off a page that no longer exists
    pages(value) {
      if (this.page > value) {
        this.page = Math.max(value, 1)
      }
    }
  },
  methods: {
    ...mapActions(appsStore, ['getApps']),
    ...mapActions(adasmatrixStore, ['getBrokenLinks', 'resetBrokenLinks']),
    formatDate(date) {
      return globals.formatDate(date, 'datetime')
    },
    refresh() {
      this.page = 1
      this.fetchLinks()
    },
    openDetail(inspection, group, link) {
      if (!group || !link) {
        return
      }
      this.detailRow = { ...inspection, ...group, ...link, members: group.links }
    },
    closeDetail() {
      this.detailRow = null
      if (this.isListingStale) {
        this.isListingStale = false
        this.fetchLinks()
      }
    },
    saveDetail() {
      this.isListingStale = true
    },
    updateDetail(row) {
      this.detailRow = row
    },
    appName(source) {
      if (!source) {
        return ''
      }
      return this.appNames[source] ? this.appNames[source].appName : source
    },
    clearFilters() {
      this.filterSources = []
      // clearing returns to the default view, not to every status
      this.filterStatus = STATUS_UNRESOLVED
      this.filterYear = null
      this.filterMake = null
      this.filterModel = null
      this.searchTerm = ''
    },
    /** The procedure nests [groups] and [links] as raw JSON strings. */
    parseJsonArray(value) {
      if (Array.isArray(value)) {
        return value
      }
      if (!value || !globals.isJson(value)) {
        return []
      }
      let parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    },
    buildSearchText(row, groups) {
      const parts = [row.adasInspection, row.vehicleMakeName, row.vehicleModel, row.vehicleYear]
      groups.forEach((group) => {
        parts.push(group.adasCategory, group.system, group.location, group.matrixModel, group.adasLinks)
        group.links.forEach((link) => parts.push(link.display, link.newDisplay, link.url))
      })
      return parts
        .filter((part) => part !== null && part !== undefined && part !== '')
        .join(' ')
        .toLowerCase()
    },
    reportKey(group, link) {
      if (!group || !link) {
        return ''
      }
      let url = (link.url || '').trim().toLowerCase()
      let name = (link.newDisplay || link.display || '').trim().toLowerCase()
      let target = url || (name ? `name:${name}` : '')
      if (!target) {
        return ''
      }
      let matrixVehicle = group.adasChecklistMatrixVehicle
      return `${matrixVehicle === null || matrixVehicle === undefined ? '' : matrixVehicle}|${target}`
    },
    buildReportCounts(inspections) {
      let counts = {}
      inspections.forEach((inspection) => {
        inspection.groups.forEach((group) => {
          group.links.forEach((link) => {
            let key = this.reportKey(group, link)
            if (key) {
              counts[key] = (counts[key] || 0) + clicksFor(link)
            }
          })
        })
      })
      return counts
    },
    matchesSearch(row) {
      const term = this.searchTerm.trim().toLowerCase()
      return term === '' || (row.searchText || '').indexOf(term) > -1
    },
    setOrder(column, sortBy) {
      this.orderBy = column.field
      this.sortBy = sortBy
      // a re-sort reshuffles every page, so start reading the new order from the top
      this.page = 1
    },
    async fetchLinks() {
      this.message = null
      if (this.selectedApps.length === 0) {
        this.message = 'No ADAS Map applications are available to fetch Repair Links from.'
        return
      }
      this.page = 1
      this.detailRow = null
      this.validations = {}
      this.isValidated = false
      await this.getBrokenLinks(this.selectedApps)
      this.isFetched = true
      if (this.brokenLinks.length === 0) {
        this.message = 'No Repair Links were found for the selected Application(s).'
      }
    },
    async validate() {
      this.message = null
      this.isValidating = true
      try {
        await this.getBrokenLinks(this.selectedApps)
        let results = {}
        let checked = 0
        let fixed = 0
        this.inspectionRows.forEach((inspection) => {
          inspection.groups.forEach((group) => {
            group.links.forEach((link) => {
              // the url is the link's own, the stored ALLDATA payload the calibration's
              let outcome = this.validateLink({ ...group, ...link })
              results[this.validationKey(inspection.source, link)] = outcome
              checked++
              if (outcome === VALIDATION_FIXED) {
                fixed++
              }
            })
          })
        })
        this.validations = results
        this.isValidated = true
        this.message = `${fixed} of ${checked} Repair Link(s) have changed since they were reported.`
      } finally {
        this.isValidating = false
      }
    },
    // ids belong to the reporting application, so they only identify a row alongside it
    validationKey(source, link) {
      return `${source || ''}-${link.adasBrokenLink}`
    },
    /** Every url the record currently holds, in both the forms a report could have stored. */
    currentLinkUrls(row) {
      let urls = []
      if (!row || !row.allData) {
        return urls
      }
      buildLinkGroups(row.allData).forEach((group) => {
        let links = group.links || []
        links.forEach((link) => {
          // the reported url is the mapped one; a manually entered link is stored as typed
          let values = [link.url, link.href]
          values.forEach((value) => {
            let url = String(value === null || value === undefined ? '' : value).trim()
            if (url && urls.indexOf(url) === -1) {
              urls.push(url)
            }
          })
        })
      })
      return urls
    },
    validateLink(row) {
      let urls = this.currentLinkUrls(row)
      let reported = String(row.url || '').trim()
      // a "No Link Built" report names no link at all, so any link on the record answers it
      if (!reported) {
        return urls.length > 0 ? VALIDATION_FIXED : VALIDATION_UNCHANGED
      }
      if (urls.length === 0) {
        return VALIDATION_NO_LINKS
      }
      let stillHeld = urls.some((url) => url === reported || reported.indexOf(url) > -1)
      return stillHeld ? VALIDATION_UNCHANGED : VALIDATION_FIXED
    },
    saveCsv() {
      this.isDownloading = true

      let columnsData = [
        { label: 'Source', field: '' },
        { label: 'ReportType', field: '' },
        { label: 'Clicks', field: '' },
        { label: 'AdasChecklistMatrixVehicle', field: 'adasChecklistMatrixVehicle' },
        { label: 'ReportedAdasCategory', field: 'adasCategory' },
        { label: 'ReportedMake', field: 'vehicleMakeName' },
        { label: 'ReportedModel', field: 'vehicleModel' },
        { label: 'YearStart', field: 'yearStart' },
        { label: 'YearEnd', field: 'yearEnd' },
        { label: 'ReportedSystem', field: 'system' },
        { label: 'ReportedLocation', field: 'location' },
        { label: 'ReportedLinkName', field: 'display' },
        { label: 'ReportedLink', field: 'url' },

        { label: 'PossibleLinkName1', field: '' },
        { label: 'PossibleLink1', field: '' },
        { label: 'PossibleLinkName2', field: '' },
        { label: 'PossibleLink2', field: '' },
        { label: 'PossibleLinkName3', field: '' },
        { label: 'PossibleLink3', field: '' },

        { label: 'LinkInfoId', field: 'adasLinks' },
        { label: 'Make', field: 'vehicleMakeName' },
        { label: 'Model', field: 'matrixModel' },
        { label: 'Year', field: 'vehicleYear' },
        { label: 'AdasCategory', field: 'adasCategory' },
        { label: 'System', field: 'system' },
        { label: 'Location', field: 'location' },
        { label: 'LinkType', field: 'linkType' },
        { label: 'LinkGroup', field: 'categoryName' },
        { label: 'LinkId', field: 'linkId' },
        { label: 'LinkName', field: '' },
        { label: 'Link', field: '' },
        { label: 'Exceptions', field: 'exceptions' }
      ]

      const exportRows = []
      this.filteredLinks.forEach((inspection) => {
        inspection.groups.forEach((group) => {
          group.links.forEach((link) => {
            exportRows.push({ ...inspection, ...group, ...link })
          })
        })
      })

      if (exportRows.length) {
        const escape = (val) => `"${(val === null || val === undefined ? '' : String(val)).replace(/"/g, '""')}"`
        const csvData = []

        // add header row
        csvData.push(columnsData.map((column) => escape(column.label)).join(','))

        // process data
        exportRows.map((row) => {
          let data = []
          let storedLink = this.storedLink(row)
          let suggestion1 = null
          let suggestion2 = null
          let suggestion3 = null
          if (row.suggestionsData && globals.isJson(row.suggestionsData)) {
            let suggestions = JSON.parse(row.suggestionsData)
            if (Array.isArray(suggestions)) {
              // the selected link is the one the admin picked, so it leads; the rest fall in by relevance
              let links = suggestions.filter(x => !x.isCustom && !isNoLinkBuiltOrFound(x))
                .sort((a, b) => (this.selectedRank(b) - this.selectedRank(a)) || (this.relevanceOf(b) - this.relevanceOf(a)))
                .slice(0, 3)
              suggestion1 = links[0] || null
              suggestion2 = links[1] || null
              suggestion3 = links[2] || null
            }
          }
          columnsData.map((column) => {
            if (column.label === 'Source') {
              data.push(escape(this.appName(row.source)))
            } else if (column.label === 'ReportType') {
              data.push(escape(this.getStatus(row, 'reportedStatus').name))
            } else if (column.label === 'Clicks') {
              // one line per link, so this is that link's own count
              data.push(escape(clicksFor(row)))
            } else if (column.label === 'LinkType') {
              data.push(escape(this.lookupTypeName(row.linkType)))
            } else if (column.label === 'PossibleLinkName1') {
              data.push(escape(suggestion1 && suggestion1.article))
            } else if (column.label === 'PossibleLink1') {
              data.push(escape(suggestion1 && suggestion1.url))
            } else if (column.label === 'PossibleLinkName2') {
              data.push(escape(suggestion2 && suggestion2.article))
            } else if (column.label === 'PossibleLink2') {
              data.push(escape(suggestion2 && suggestion2.url))
            } else if (column.label === 'PossibleLinkName3') {
              data.push(escape(suggestion3 && suggestion3.article))
            } else if (column.label === 'PossibleLink3') {
              data.push(escape(suggestion3 && suggestion3.url))
            } else if (column.label === 'LinkName') {
              data.push(escape(storedLink && storedLink.name))
            } else if (column.label === 'Link') {
              data.push(escape(storedLink && (storedLink.href || storedLink.url)))
            } else {
              data.push(escape(row[column.field]))
            }
          })
          csvData.push(data.join(','))
        })

        const csvString = csvData.join('\n')
        var blob = new Blob([csvString], { type: 'text/csv' })
        var link = document.createElement('a')
        var now = moment().format('MMDDYYYY')
        var label = this.selectedApps.length === 1 ? this.appName(this.selectedApps[0]) : 'Multiple'
        if (window.URL.createObjectURL) {
          link.href = window.URL.createObjectURL(blob)
          link.download = `Repair Links ${label} ${now}.csv`
          link.click()
        } else {
          this.message = 'Refresh your browser to generate this download.'
        }
      }
      this.isDownloading = false
    },
    storedLink(row) {
      if (!row || !row.allData) {
        return null
      }
      // a repaired link carries its new name, so that one is matched first
      const names = [row.newDisplay, row.display].filter((name) => name)
      let byName = null
      let byUrl = null
      buildLinkGroups(row.allData).forEach((group) => {
        group.links.forEach((link) => {
          if (!byName && names.indexOf(link.name) > -1) {
            byName = link
          }
          if (!byUrl && row.url && link.url === row.url) {
            byUrl = link
          }
        })
      })
      return byName || byUrl
    },
    relevanceOf(data) {
      return data && typeof data.relevance === 'number' ? data.relevance : -Infinity
    },
    selectedRank(data) {
      return data && data.isSelected ? 1 : 0
    },
    lookupTypeName(lookupType) {
      let found = LOOKUP_TYPES.find((x) => x.value === parseInt(lookupType))
      return found ? found.name : ''
    },
    getStatus(data, type = '') {
      return getBrokenLinkStatus(type === '' ? data.status : data.reportedStatus)
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-repair-wrapper {
  width: 100%;
}
.custom-spinner {
  width: 100%;
  text-align: center;
  font-size: 1.1em;
  min-height: 40px;
}
.listingWrapper {
  position: relative;
  overflow-y: auto;
  overflow-x: auto;
  // max-height: 500px;
  width: 100%;
}
.custom-column-actions {
  width: 90px;
  white-space: nowrap;
}
.custom-filter {
  flex: 1 1 0;
  width: auto;
}
.custom-filter-clear {
  flex: 0 0 auto;
}
.custom-page-size {
  width: 150px;
}
// the bootstrap pagination list carries a bottom margin that would push the listing down
.custom-pagination :deep(.pagination) {
  margin-bottom: 0;
}
// size the multiselect like the bootstrap selects beside it - the form-control
// padding would otherwise stack on top of the multiselect's own min-height
.custom-multiselect {
  padding: 0;
  min-height: 38px;
  --ms-px: 0.75rem;
  --ms-radius: 0.375rem;
  --ms-border-color: #ced4da;
}
.custom-multiselect :deep(.multiselect-wrapper) {
  // 38px control less its 1px borders
  min-height: 36px;
}
</style>
